import React, { useState } from 'react';
import { Gift, Check, X, Loader2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import api from '../lib/api';

interface PromoCodeInputProps {
  onSuccess?: (credits: { basic: number; enhanced: number }) => void;
}

const PromoCodeInput: React.FC<PromoCodeInputProps> = ({ onSuccess }) => {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const handleRedeem = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!code.trim()) {
      setMessage({ type: 'error', text: 'Please enter a promo code' });
      return;
    }

    setLoading(true);
    setMessage(null);

    try {
      const data = await api.request<{ credits: { basic: number; enhanced: number } }>('/api/promo/redeem', {
        method: 'POST',
        body: JSON.stringify({ code: code.trim() }),
      });

      if (data.success && data.data) {
        setMessage({
          type: 'success',
          text: `Success! Added ${data.data.credits.basic} Basic + ${data.data.credits.enhanced} Enhanced credits`,
        });
        setCode('');
        
        if (onSuccess) {
          onSuccess(data.data.credits);
        }

        // Clear success message after 5 seconds
        setTimeout(() => setMessage(null), 5000);
      } else {
        setMessage({
          type: 'error',
          text: data.error || 'Failed to redeem promo code',
        });
      }
    } catch (error) {
      console.error('Promo code redemption error:', error);
      setMessage({
        type: 'error',
        text: 'Network error. Please try again.',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="promo-code-section">
      <div className="promo-code-header">
        <Gift className="icon" />
        <h3>Have a Promo Code?</h3>
      </div>

      <form onSubmit={handleRedeem} className="promo-code-form">
        <div className="input-group">
          <input
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder="Enter promo code"
            disabled={loading}
            className="promo-input"
            maxLength={50}
          />
          <button
            type="submit"
            disabled={loading || !code.trim()}
            className="redeem-button"
          >
            {loading ? (
              <>
                <Loader2 className="icon spin" />
                Redeeming...
              </>
            ) : (
              'Redeem'
            )}
          </button>
        </div>

        <AnimatePresence>
          {message && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className={`message ${message.type}`}
            >
              {message.type === 'success' ? (
                <Check className="icon" />
              ) : (
                <X className="icon" />
              )}
              <span>{message.text}</span>
            </motion.div>
          )}
        </AnimatePresence>
      </form>

      <style>{`
        .promo-code-section {
          background: linear-gradient(135deg, rgba(99, 102, 241, 0.1), rgba(168, 85, 247, 0.1));
          border: 1px solid rgba(99, 102, 241, 0.2);
          border-radius: 16px;
          padding: 24px;
          margin-bottom: 32px;
        }

        .promo-code-header {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 16px;
        }

        .promo-code-header .icon {
          width: 24px;
          height: 24px;
          color: white;
        }

        .promo-code-header h3 {
          font-size: 18px;
          font-weight: 600;
          color: white;
          margin: 0;
        }

        .promo-code-form {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .input-group {
          display: flex;
          gap: 12px;
        }

        .promo-input {
          flex: 1;
          padding: 12px 16px;
          border: 2px solid #e5e7eb;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 500;
          text-transform: uppercase;
          letter-spacing: 1px;
          transition: all 0.2s;
        }

        .promo-input:focus {
          outline: none;
          border-color: #6366f1;
          box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
        }

        .promo-input:disabled {
          background: #f9fafb;
          cursor: not-allowed;
        }

        .redeem-button {
          padding: 12px 24px;
          background: linear-gradient(135deg, #6366f1, #8b5cf6);
          color: white;
          border: none;
          border-radius: 8px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
          display: flex;
          align-items: center;
          gap: 8px;
          white-space: nowrap;
        }

        .redeem-button:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 8px 16px rgba(99, 102, 241, 0.3);
        }

        .redeem-button:disabled {
          opacity: 0.5;
          cursor: not-allowed;
          transform: none;
        }

        .redeem-button .icon {
          width: 18px;
          height: 18px;
        }

        .redeem-button .spin {
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        .message {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 12px 16px;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 500;
        }

        .message.success {
          background: #d1fae5;
          color: #065f46;
          border: 1px solid #6ee7b7;
        }

        .message.error {
          background: #fee2e2;
          color: #991b1b;
          border: 1px solid #fca5a5;
        }

        .message .icon {
          width: 18px;
          height: 18px;
          flex-shrink: 0;
        }

        @media (max-width: 640px) {
          .input-group {
            flex-direction: column;
          }

          .redeem-button {
            width: 100%;
            justify-content: center;
          }
        }
      `}</style>
    </div>
  );
};

export default PromoCodeInput;
